// Canvas marketplace page — reuses components from the marketplace shared
// library (MarketplaceNavbar, MarketplaceSidebar, MarketplaceFilters,
// MarketplaceGrid, MarketplaceDetail) but wraps them in the same
// AppSidebar + AppMain chrome as /dashboard so the layout stays consistent
// across canvas routes.
//
// This page is flagged `_marketplaceHost: true` and carries its own
// marketplace state (catalog/selectedItem/installed/etc.) so `marketplaceHostOf`
// binds every marketplace component to THIS element — not canvas's app root.
// Without the flag, state falls through to canvas root (shared with
// assistant / command bar / workspace) and polluted-or-missing fields leave
// the grid empty. Seeds `catalog` from the static list so the grid renders
// immediately pre-hydration (signed-in users get merged DB kinds on top via
// `hydrateLiveCatalog` in MarketplaceGrid.onCreate).
//
// Deep-link: `/marketplace?kind=registrar&categories=dns,domains&q=porkbun&sort=name&pricing=free&item=int.plausible`
// pre-seeds the sidebar/filter/sort state AND opens the detail pane
// so a shared URL reproduces someone else's filtered view exactly.
// URL round-trip is bidirectional: reader on mount + popstate,
// writer on every state change via `writeFiltersToUrl` in onUpdate.
// Also see the in-Navbar `Copy link` button + the `S` shortcut for
// explicit share flows.

// Pull catalog + URL-filter parser from shared/ — both surfaces (canvas's
// /marketplace preview and the marketplace sub-app's main page) consume
// the same source, so cross-importing across sub-apps is no longer needed.
import marketplaceCatalog from '../../shared/data/marketplaceCatalog.js'
import { parseFiltersFromSearch } from '../../shared/functions/marketplaceFilters.js'

// Shallow array-equality for activeCategories diff detection.
const _arrEq = (a, b) =>
  Array.isArray(a) && Array.isArray(b)
  && a.length === b.length
  && a.every((x, i) => x === b[i])

// Reads `currentSearch` from canvas root (updated by AppShell's
// locationchange patch on pushState/replaceState), parses URL filters,
// and applies only the diffs to the island's state. Gated on
// `el._lastCurrentSearch` so in-session state changes (chip clicks,
// kind selections, etc.) don't trigger the reader — only actual URL
// changes (initial mount + popstate) do. Without this gate, the
// `activeKind → 'all'` default below would clobber user sidebar clicks
// every time onUpdate fires.
const _applyFiltersFromUrl = (el, s) => {
  const root = s?.root
  const host = el.state
  if (!host) return
  const currentSearch = root?.currentSearch || ''
  if (el._lastCurrentSearch === currentSearch) return
  el._lastCurrentSearch = currentSearch
  const next = parseFiltersFromSearch(currentSearch)
  // Kind defaults to 'all' when not in URL so sidebar resets on
  // back/forward navigation to the base /marketplace URL.
  if (next.activeKind === undefined) next.activeKind = 'all'

  // `_itemId` is synthetic (not a real host state key) — resolve it
  // against the catalog and translate into selectedItem mutation.
  // Missing param → clear selectedItem so popstate back to /marketplace
  // closes any open detail. Pull the translated update out of the
  // generic diff loop since it's a two-field transaction.
  const itemId = next._itemId
  delete next._itemId
  const currentItemId = host.selectedItem?.id || null
  let itemDiff = null
  if (itemId && itemId !== currentItemId) {
    const found = (host.catalog || []).find((i) => i.id === itemId)
    if (found) itemDiff = { selectedItem: found, selectedHistory: [] }
  } else if (!itemId && currentItemId) {
    itemDiff = { selectedItem: null, selectedHistory: [] }
  }

  const diff = {}
  for (const [k, v] of Object.entries(next)) {
    if (k === 'activeCategories') {
      if (!_arrEq(v, host[k])) diff[k] = v
    } else if (host[k] !== v) {
      diff[k] = v
    }
  }
  const merged = { ...diff, ...(itemDiff || {}) }
  if (Object.keys(merged).length > 0) host.update(merged)
}

export const marketplace = {
  tag: 'main',
  id: 'main-content',
  extends: 'AppPage',
  metadata: { title: 'Marketplace / Symbols.app' },
  gridRow: '2',
  gridColumn: '1 / -1',
  minHeight: '0',
  height: '100%',
  overflow: 'hidden',
  color: 'title',

  // Host marker: `marketplaceHostOf(el)` walks the parent chain looking
  // for this flag — first match wins. Isolates marketplace state from
  // canvas root state.
  _marketplaceHost: true,
  state: {
    // Multi-select categories (see MarketplaceIsland + toggleCategory).
    activeCategories: [],
    activeKind: 'all',
    activePricing: 'all',
    activeSort: 'name',
    gridDensity: 'comfortable',
    query: '',
    selectedItem: null,
    selectedHistory: [],
    sidebarMode: false,
    installing: null,
    installed: [],
    pinnedIds: [],
    recentIds: [],
    toastMessage: null,
    toastUndoItemId: null,
    shortcutsOpen: false,
    catalog: marketplaceCatalog,
    // Install modal state — null when closed, { item, step, draft } when open.
    installModal: null,
    _installModalSaving: false,
    _installModalError: null
  },

  onCreate: (el, s) => {
    _applyFiltersFromUrl(el, s)
    // Bind global Esc → close detail pane for this host. Idempotent
    // (see `registerEscHandler` in marketplace/functions/index.js).
    el.call('registerEscHandler', el.state)
  },
  // `currentSearch` updates on history pushState/replaceState via
  // AppShell's locationchange patch — re-read so navigating between
  // `/marketplace?kind=A&q=x` and `/marketplace?kind=B` inside the same
  // SPA session keeps the filter UI in sync. Writer runs AFTER the
  // reader so chip/sidebar clicks that update state immediately sync
  // back to the URL — keeping the address bar shareable at all times.
  onUpdate: (el, s) => {
    _applyFiltersFromUrl(el, s)
    el.call('writeFiltersToUrl', el.state)
  },

  AppSidebar: {
    MarketplaceSidebar: {
      width: 'auto',
      minWidth: 'auto',
      border: '0'
    }
  },

  AppMain: {
    padding: '0',
    overflow: 'hidden',
    // `minHeight: 0` lets Body actually respect the AppMain-provided
    // height cap; without it AppMain's grid-flex context lets children
    // overflow freely, breaking the row-4 `1fr` constraint below — the
    // Grid cell would then size to content, and `overflow: y auto` on
    // the Grid couldn't trigger because nothing was actually overflowing.
    minHeight: '0',

    Body: {
      padding: '0',
      overflow: 'hidden',
      display: 'grid',
      gridTemplateRows: 'auto auto auto 1fr',
      gridTemplateColumns: '1fr auto',
      minHeight: '0',
      height: '100%',
      // Positioning context for MarketplaceToast (absolute bottom-right).
      position: 'relative',

      MarketplaceNavbar: {
        gridRow: '1',
        gridColumn: '1 / -1'
      },

      MarketplaceFilters: {
        gridRow: '2',
        gridColumn: '1 / -1'
      },

      MarketplaceResultsBar: {
        gridRow: '3',
        gridColumn: '1 / -1'
      },

      MarketplaceGrid: {
        gridRow: '4',
        gridColumn: '1',
        overflow: 'y auto',
        alignContent: 'start',
        // Grid cells default to `min-content` — must set `minHeight: 0`
        // so `overflow: y auto` has a constrained box to scroll within.
        minHeight: '0'
      },

      MarketplaceDetail: {
        gridRow: '4',
        gridColumn: '2',
        minHeight: '0'
      },

      // Transient install-confirm toast. Absolute-positioned — doesn't
      // reflow the grid rows above. Hidden via `host.toastMessage`
      // gate when no toast is active.
      MarketplaceToast: {},

      // Help overlay (toggled by `?` key).
      MarketplaceShortcutsOverlay: {},

      // Install modal — fixed overlay, gated on host.installModal. Placed
      // inside the page Body so `marketplaceHostOf` walks up and finds
      // the _marketplaceHost flag on the canvas marketplace page element.
      MarketplaceInstallModal: {}
    }
  }
}
